import { formatDateTime } from "@/lib/format";
import { useEffect, useState, useCallback } from "react";
import { glopicApi } from "@/lib/glopic-api";
import Pagination from "@/components/base/Pagination";
import FilterBar from "@/components/base/FilterBar";
import TableSortHeader from "@/components/base/TableSortHeader";
import UserIdentityCell from "@/components/base/UserIdentityCell";

interface ImageAuditRow {
  id: number;
  gen_id: string | null;
  user_id: string | null;
  nickname: string | null;
  avatar: string | null;
  image_url: string | null;
  display_image_url: string | null;
  original_image_url: string | null;
  image_url_type: string | null;
  result: string;
  risk_level: string | null;
  risk_type: string | null;
  provider: string | null;
  latency_ms: number | null;
  created_at: string;
}

const resultLabels: Record<string, { cls: string; label: string }> = {
  pass: { cls: "bg-emerald-500/10 text-emerald-500", label: "通过" },
  block: { cls: "bg-red-500/10 text-red-500", label: "拦截" },
};

const riskLabels: Record<string, string> = {
  low: "低风险",
  medium: "中风险",
  high: "高风险",
};

export default function AiAuditImagesPage() {
  const [audits, setAudits] = useState<ImageAuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [resultFilter, setResultFilter] = useState<string>("");
  const [riskFilter, setRiskFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const handleSort = useCallback((field: string, dir: "asc" | "desc") => {
    setSortField(field || "created_at");
    setSortDir(dir);
    setPage(1);
  }, []);

  const fetchAudits = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, page_size: pageSize };
      if (resultFilter) params.result = resultFilter;

      const response = await glopicApi.get("/audit/images", { params });
      const data = response.data.data;
      setAudits(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      console.error("Fetch image audits error:", err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultFilter, riskFilter, sortField, sortDir, page, pageSize]);

  useEffect(() => {
    fetchAudits();
  }, [fetchAudits]);

  const totalPages = Math.ceil(total / pageSize);
  const passCount = audits.filter((a) => a.result === "pass").length;
  const blockCount = audits.filter((a) => a.result === "block").length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">图片审核日志</h2>
          <p className="text-text-secondary text-sm mt-1">
            共 {total} 次检测 · 通过 {passCount} · 拦截 {blockCount}
          </p>
        </div>
      </div>

      {/* Filters */}
      <FilterBar
        filters={[
          {
            key: "result",
            label: "全部结果",
            type: "select",
            options: [
              { value: "pass", label: "通过" },
              { value: "block", label: "拦截" },
            ],
          },
          {
            key: "risk",
            label: "全部风险",
            type: "select",
            options: [
              { value: "low", label: "低风险" },
              { value: "medium", label: "中风险" },
              { value: "high", label: "高风险" },
            ],
          },
        ]}
        filterValues={{ result: resultFilter, risk: riskFilter }}
        onFilterChange={(key, val) => {
          if (key === "result") setResultFilter(val);
          if (key === "risk") setRiskFilter(val);
          setPage(1);
        }}
        onReset={() => { setResultFilter(""); setRiskFilter(""); setSortField("created_at"); setSortDir("desc"); setPage(1); }}
      />

      {/* Table */}
      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="overflow-x-auto -mx-0">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">用户</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">结果</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">风险等级</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">风险类型</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">提供商</th>
                <TableSortHeader label="延迟" field="latency_ms" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                <TableSortHeader label="时间" field="created_at" currentField={sortField} currentDir={sortDir} onSort={handleSort} className="hidden sm:table-cell" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />
                    加载中...
                  </td>
                </tr>
              ) : audits.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-shield-check-line text-3xl mb-2 block" />
                    暂无审核记录
                  </td>
                </tr>
              ) : (
                audits.map((a) => {
                  const resultCfg = resultLabels[a.result] || resultLabels.pass;
                  return (
                    <tr key={a.id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                      <td className="px-3 md:px-5 py-3 md:py-4">
                        <UserIdentityCell nickname={a.nickname} userId={a.user_id} avatar={a.avatar} />
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${resultCfg.cls}`}>
                          <i className={a.result === "pass" ? "ri-checkbox-circle-line" : "ri-close-circle-line"} />
                          {resultCfg.label}
                        </span>
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-text-secondary text-xs">
                        {a.risk_level ? riskLabels[a.risk_level] || a.risk_level : "--"}
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-text-secondary text-xs">
                        {a.risk_type || "--"}
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-text-muted text-xs">
                        {a.provider || "--"}
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4 font-mono text-text-primary text-xs">
                        {a.latency_ms ? `${a.latency_ms}ms` : "--"}
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-text-muted text-xs hidden sm:table-cell">
                        {formatDateTime(a.created_at)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
